import React, { useState, useEffect } from "react";
import { css, Global } from "@emotion/react";
import { ThemeProvider } from "@emotion/react";
import Helmet from "react-helmet";
import Header from "@src/components/Header";
import Footer from "@src/components/Footer";
import Modal from "react-modal";
import normalize from "../styles/normalize";
import theme from "../styles/theme";
import { ModalProvider, ModalRoot } from "@src/components/modals/ModalController";
import { TranslationProvider } from "@src/components/translation/TranslationContext";
import { AppDataContextProvider } from "@src/components/AppDataContext";
import OneTrustScript from "@src/components/partials/shared/OneTrustScript";
// import "modern-normalize/modern-normalize.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useLocation } from "@reach/router";
import { generatingFor } from "@src/utils/routes";
import StyledLayoutMain from "@src/layouts/common/StyledLayoutMain";
import SkipToContentLink from "@src/components/SkipToContentLink";
import TrackingPixels from "@src/components/partials/header/TrackingPixels";
import { DevTools } from "@src/components/devTools";
import { ActivationRedirectionProvider } from "@src/contexts/ActivationRedirectionContext";
import { logEvent } from "@src/utils/logClient";
import { useTranslationsRepoDefault } from "@src/components/translation/useTranslationsRepo";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import PlausibleScript from "@src/components/partials/shared/PlausibleScript";
// import "../../vendor/mds/dist/css/mds.css";

Modal.setAppElement("#___gatsby");

type PageLayoutProps = {
  children: any;
  pageContext: any;
};
const urlsWithUnpinnedHeader = ["/dojo-islands"];
const urlsWithoutNav = [
  "/teacherlp",
  "/teacherlp2",
  "/teacherlp3",
  "/teacherlp4",
  "/teacherlp5",
  "/teacherlp6",
  "/teacherlp7",
  "/encouragelp",
  "/googleclassroom",
  "/schoolleader2",
  "/student-login-w-parent-help",
  "/homePoints",
  "/homeRewards",
  "/homeMonsters",
  "/homeMonsterCustomizer",
  "/tryitout",
  "/become-mentor-application",
  "/email/manage",
  "/email/confirm",
];
const urlsWithoutHeader = [
  "/dojo-islands",
  "/dojo-islands/play",
  "/dojo-islands/connect-phone",
  "/play-dojo-islands",
  "/play-dojo-islands/consent",
  "/play-dojo-islands/consent-error",
  "/ecard",
  "/beyondschool",
  "/share-the-news",
  "/become-mentor-application",
  /\/lp\/\w/,
  /\/tutoring(\/|$)/,
  /\/online-classes(\/|$)/,
  /\/dojo-tutor\/lp\d/,
  "/dojo-tutor",
  "/dojo-tutor/become-a-tutor",
  // Dojo Tutor landing pages ship their own simplified nav (see LpNavigation).
  "/stride",
  "/clubs",
  "/homework-squad",
  "/esa/arizona",
  /\/email\//,
  // The /dojo-islands/* section ported from islands.classdojo.com brings its
  // own header and Footer, so the global ones are dropped. Enumerated rather
  // than a blanket /dojo-islands/ prefix: `parents` and `teachers` deliberately
  // keep the global header (they layer their own on top of it), and
  // `play` / `connect-phone` already have their own entries. Built in every
  // locale (see src/creators/dojo-islands.ts), hence the optional prefix.
  /^(\/[a-z]{2,3}-[a-z]{2})?\/dojo-islands\/(news|library|rangers|the-islands|adventure-pass|welcome|play-free)(\/|$)/,
  // Parents and Teachers render the Islands section header themselves (in
  // place of the global one), so clicking them from the Islands nav keeps you
  // on the Islands nav. They keep the global footer. These are file-based
  // pages, so they also have locale-prefixed copies.
  /^(\/[a-z]{2,3}-[a-z]{2})?\/dojo-islands\/(parents|teachers)(\/|$)/,
];

const urlsWithoutFooter = [
  "/dojo-islands",
  "/dojo-islands/play",
  "/dojo-islands/connect-phone",
  "/play-dojo-islands",
  "/play-dojo-islands/consent",
  "/play-dojo-islands/consent-error",
  "/student-login-w-parent-help",
  "/share-the-news",
  "/become-mentor-application",
  /\/tutoring(\/|$)/,
  /\/email\//,
  /\/online-classes(\/|$)/,
  // The /dojo-islands/* section ported from islands.classdojo.com brings its
  // own header and Footer, so the global ones are dropped. Enumerated rather
  // than a blanket /dojo-islands/ prefix: `parents` and `teachers` deliberately
  // keep the global header (they layer their own on top of it), and
  // `play` / `connect-phone` already have their own entries. Built in every
  // locale (see src/creators/dojo-islands.ts), hence the optional prefix.
  /^(\/[a-z]{2,3}-[a-z]{2})?\/dojo-islands\/(news|library|rangers|the-islands|adventure-pass|welcome|play-free)(\/|$)/,
];

const isNavHidden = (pathname: string) => {
  return urlsWithoutNav.some((url) => pathname.endsWith(url));
};

const isHeaderHidden = (pathname: string) => {
  return urlsWithoutHeader.some((url) => {
    if (typeof url === "string") {
      return pathname.endsWith(url);
    }

    return url.test(pathname);
  });
};

const isFooterHidden = (pathname: string) => {
  return urlsWithoutFooter.some((url) => {
    if (typeof url === "string") {
      return pathname.endsWith(url);
    }
    return url.test(pathname);
  });
};

const isDojoTutorPath = (pathname: string) => /\/dojo-tutor(?:\/|$)/.test(pathname);

const isHeaderUnpinned = (pathname: string) => {
  return urlsWithUnpinnedHeader.some((url) => pathname.endsWith(url));
};

// Pages whose hero is designed to sit behind a transparent header that turns solid
// once scrolled past (see Header's transparentHero prop). `lightContent` whitens the logo
// and nav labels for heroes with a dark background; pages with a light hero keep the
// default dark content colors.
const urlsWithTransparentHeroHeader: { url: string | RegExp; lightContent: boolean }[] = [
  { url: "/school-leaders", lightContent: true },
  // The entries below are anchored (unlike the endsWith entries above) so they match a
  // page and its locale-prefixed variants (/es-es/teachers) without also catching
  // unrelated nested routes like /dojo-islands/teachers. `pathname` already has its
  // trailing slash stripped above.
  { url: /^(\/[a-z]{2,3}-[a-z]{2})?\/teachers$/, lightContent: false },
  { url: /^(\/[a-z]{2,3}-[a-z]{2})?\/families$/, lightContent: false },
];

const getTransparentHeroHeader = (pathname: string) => {
  return urlsWithTransparentHeroHeader.find(({ url }) =>
    typeof url === "string" ? pathname.endsWith(url) : url.test(pathname),
  );
};

// Pages whose hero is a solid dark purple band that starts at the top of the page, so a white
// header on it reads as a stray box: the header turns dark purple with light content (see
// Header's darkHeader prop). Anchored like the entries above; `pathname` has no trailing slash.
const urlsWithDarkHeader: RegExp[] = [/^\/districts$/, /^\/districts\/family-engagement-report$/];

const isDarkHeader = (pathname: string) => urlsWithDarkHeader.some((url) => url.test(pathname));

// Any page under /sparks gets the Dojo Sparks logo + "Free week" CTA instead of the
// default ClassDojo branding (see Header's sparksBrand prop).
const isSparksBrand = (pathname: string) => pathname === "/sparks" || pathname.startsWith("/sparks/");

const PageLayout: React.FC<PageLayoutProps> = ({ children, pageContext }) => {
  const [, setIsUserInAudience] = useState(false);
  const [navBannerOpen, setNavBannerOpen] = useState<boolean>(false);
  const handleIsUserInAudience = (newValue: boolean) => {
    setIsUserInAudience(newValue);
  };

  const localeRepo = useTranslationsRepoDefault(pageContext.localeStrings);

  useEffect(() => {
    const bannerDismissed = localStorage.getItem("bannerDismissed");
    if (bannerDismissed === "true") {
      setNavBannerOpen(false);
    } else {
      setNavBannerOpen(true);
    }
  }, []);

  // Use the i18n instance from context (not the global instance)
  // This ensures both SSR and browser use the correct locale
  const { i18n: i18nFromContext } = useTranslation();
  const lang = i18nFromContext.language;
  generatingFor.locale = lang;
  // const targetCDSLocales = ["en", "en-gb", "pt-br", "es-es", "es-mx", "fr-fr", "fr-ca", "tr-tr"];
  // const isLocaleSupported = targetCDSLocales.includes(lang.toLowerCase());

  const location = useLocation();
  const pathname = location.pathname.endsWith("/") ? location.pathname.slice(0, -1) : location.pathname;

  const closeBanner = () => {
    setNavBannerOpen(false);
    localStorage.setItem("bannerDismissed", "true");
    logEvent("web.external.navbanner.close");
  };
  // const showNavBanner = (pathname === "" || pathname === "/schools") && navBannerOpen;
  const showNavBanner = false;

  const hideNav = isNavHidden(pathname);
  const hideHeader = isHeaderHidden(pathname);
  const hideFooter = isFooterHidden(pathname);
  const unpinHeader = isHeaderUnpinned(pathname);
  const transparentHeroHeader = getTransparentHeroHeader(pathname);
  const darkHeader = isDarkHeader(pathname);
  const sparksBrand = isSparksBrand(pathname);
  const [performanceCookieConsent, setPerformanceCookieConsent] = useState(false);
  const [targetingCookieConsent, setTargetingCookieConsent] = useState(false);
  const [functionalCookieConsent, setFunctionalCookieConsent] = useState(false);

  if (
    pathname.includes("/ul/") ||
    pathname.includes("/school-approval") ||
    pathname.includes("/b/") ||
    pathname === "/s" ||
    pathname === "/q"
  ) {
    return (
      <>
        <TranslationProvider localeRepo={localeRepo}>
          <OneTrustScript
            setFunctionalCookieConsent={setFunctionalCookieConsent}
            setPerformanceCookieConsent={setPerformanceCookieConsent}
            setTargetingCookieConsent={setTargetingCookieConsent}
          />
          {children}
        </TranslationProvider>
      </>
    );
  }
  return (
    <>
      <Helmet htmlAttributes={{ lang }} />
      <OneTrustScript
        setFunctionalCookieConsent={setFunctionalCookieConsent}
        setPerformanceCookieConsent={setPerformanceCookieConsent}
        setTargetingCookieConsent={setTargetingCookieConsent}
      />
      <Global
        styles={css`
          // @layer reset {
          //   ${normalize};
          // }
          #___gatsby,
          #gatsby-focus-wrapper,
          main,
          html,
          body {
            // height: 100%;
            scroll-behavior: smooth;
          }
          /* modal transitions:  */
          .ReactModal__Content {
            opacity: 0;
            transform: translate(0, -50%);
            transition: transform 300ms ease-out;
          }

          .ReactModal__Content--after-open {
            opacity: 1;
            transform: translate(0, 0);
          }

          .ReactModal__Content--before-close {
            opacity: 0;
            transform: translate(0, -50%);
          }
          body {
            font-family: "Ginto", "Helvetica Neue", sans-serif;
            font-synthesis: none;
            -moz-font-feature-settings: "kern";
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }
          :root {
            interpolate-size: allow-keywords;
            /* This site has no dark theme — without this, browsers that auto-darken
               content for prefers-color-scheme: dark (e.g. Chrome's "Auto Dark Mode")
               will recolor things like icons and hover states inconsistently. */
            color-scheme: light;
          }
          :root:has(.ReactModal__Body--open) {
            overflow: hidden;
          }
        `}
      />
      <TranslationProvider localeRepo={localeRepo}>
        <ThemeProvider theme={theme}>
          <AppDataContextProvider
            performanceCookieConsent={performanceCookieConsent}
            targetingCookieConsent={targetingCookieConsent}
            functionalCookieConsent={functionalCookieConsent}
          >
            <ActivationRedirectionProvider>
              <ModalProvider>
                <>
                  <SkipToContentLink />
                  <ModalRoot />
                  <StyledLayoutMain id="main-page-container">
                    {!hideHeader && (
                      <Header
                        handleIsUserInAudience={handleIsUserInAudience}
                        showNavBanner={showNavBanner}
                        inSchools={pathname === "/schools"}
                        closeBannerFunction={closeBanner}
                        hideNav={hideNav}
                        unpinHeader={unpinHeader}
                        simplifiedHeader={[
                          "/classroom-management",
                          "/sl-schoolwide-pbis",
                          "/campaign-schools",
                          "/campaign-pbis",
                          "/school-leaders/campaign",
                        ].includes(pathname)}
                        transparentHero={!!transparentHeroHeader}
                        transparentHeroLightContent={transparentHeroHeader?.lightContent}
                        darkHeader={darkHeader}
                        sparksBrand={sparksBrand}
                      />
                    )}
                    <div id="page-content" tabIndex={-1}>
                      {children}
                    </div>
                    {!hideFooter && <Footer legalVariant={isDojoTutorPath(pathname) ? "tutor" : "default"} />}
                    <PlausibleScript
                      performanceCookieConsent={performanceCookieConsent}
                      language={lang}
                      pathname={pathname}
                    />
                    <TrackingPixels targetingCookieConsent={targetingCookieConsent} />
                    <DevTools />
                  </StyledLayoutMain>
                </>
              </ModalProvider>
            </ActivationRedirectionProvider>
          </AppDataContextProvider>
        </ThemeProvider>
      </TranslationProvider>
    </>
  );
};

export default PageLayout;
